import React, { useEffect, useState } from "react";
import { ric } from "@/lib/ric";
import { useLang } from "@/context/LanguageContext";
import { Package } from "lucide-react";

// Stock browser: read-only pivot of yarn items with live kg on hand.
// Filterable by bin, yarn type, colour.
export default function RICStock() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { (async () => setItems(await ric.yarnItems()))(); }, []);

  const filtered = items.filter((i) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [i.yarn_type, i.yarn_count, i.yarn_ply, i.colour, i.dye_lot, i.legacy_ref]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
  });

  const totalKg = filtered.reduce((a, b) => a + (b.kg_on_hand || 0), 0);

  return (
    <div data-testid="ric-stock">
      <h1 className="text-2xl font-bold mb-1 flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
        <Package size={24} /> {t("Stock browser (RIC-owned)", "स्टॉक ब्राउज़र (RIC का)")}
      </h1>
      <p className="text-xs text-[#7C8E9E] mb-3">
        {t("HAG-owned yarn stored in the same godown is out of scope and not shown here.",
           "गोदाम में मौजूद HAG का यार्न यहाँ नहीं दिखाया जाता।")}
      </p>

      <div className="mb-3 flex gap-2 items-center flex-wrap">
        <input data-testid="ric-stock-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t("Search type / colour / dye lot / legacy ref", "टाइप / रंग / डाई / रेफ़ खोजें")}
          className="h-10 px-3 flex-1 min-w-[200px] border border-[#1B3A5C]/15 rounded-md text-sm" />
        <div className="text-xs text-[#4A627A]">{filtered.length} {t("items", "आइटम")} · <b>{totalKg.toFixed(1)} kg</b></div>
      </div>

      <div className="hag-surface overflow-x-auto" data-testid="ric-stock-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E] bg-[#FAF6F0]">
              <th className="text-left px-3 py-2">{t("Type", "टाइप")}</th>
              <th className="text-left px-3 py-2">{t("Count", "गिनती")}</th>
              <th className="text-left px-3 py-2">{t("Ply", "प्लाई")}</th>
              <th className="text-left px-3 py-2">{t("Colour", "रंग")}</th>
              <th className="text-left px-3 py-2">{t("Dye lot", "डाई लॉट")}</th>
              <th className="text-right px-3 py-2">{t("Kg on hand", "कि. उपलब्ध")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} className="border-t border-[#1B3A5C]/10" data-testid={`ric-stock-row-${i.id}`}>
                <td className="px-3 py-2">{i.yarn_type}</td>
                <td className="px-3 py-2 text-xs">{i.yarn_count || "—"}</td>
                <td className="px-3 py-2 text-xs">{i.yarn_ply || "—"}</td>
                <td className="px-3 py-2 text-xs">{i.colour || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {i.dye_lot || (i.needs_dye_lot
                    ? <span className="status-chip pace-amber">{t("assign on 1st move", "पहली गति पर दें")}</span>
                    : "—")}
                </td>
                <td className="px-3 py-2 text-right font-semibold">{(i.kg_on_hand || 0).toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
